import { D, F } from "@mobily/ts-belt";
import PropTypes from "prop-types";
import * as React from "react";
import { SignType } from "../../../domain";
import { defaultFilter, useFilters } from "../utils";
import FilterSection, { FilterSectionSkeleton } from "./FilterSection";
import PriceRangeFilter from "./PriceRangeFilter";
import s from "./FilterPanel.module.css";

const getId = D.get("id");
const getLabel = D.get("label");

export default function FilterPanel({ filter, setFilter, showFilters }) {
  const { filters, isLoading } = useFilters();

  const setFilterAndRestoreRank = React.useCallback(
    (fn) => setFilter((filter) => D.merge(fn(filter), { cursor: "" })),
    [setFilter],
  );

  const handleClearAll = React.useCallback(() => {
    setFilter(defaultFilter);
  }, [setFilter]);

  const makeSetSelected = React.useCallback(
    (filterKey) =>
      ({ value, key }) => {
        if (value) {
          setFilterAndRestoreRank(D.update(filterKey, D.set(key, value)));
        } else {
          setFilterAndRestoreRank(D.update(filterKey, D.deleteKey(key)));
        }
      },
    [setFilterAndRestoreRank],
  );

  const isFiltered = !F.equals(filter, defaultFilter);

  return (
    <aside className={s.panel} aria-label="Product filters">
      <div className={s.panelHeader}>
        <h2 className={s.panelTitle}>Filters</h2>
        {isFiltered && (
          <button type="button" className={s.clearBtn} onClick={handleClearAll}>
            Clear
          </button>
        )}
      </div>

      <div
        className={`${s.collapse}${showFilters ? ` ${s.collapseOpen}` : ""}`}
      >
        <div className={s.collapseInner}>
          {isLoading ? (
            <FilterSectionSkeleton width="55%" />
          ) : (
            Boolean(filters.franchises.length) && (
              <FilterSection
                options={filters.franchises}
                label="Franchises"
                getOptionLabel={getLabel}
                getOptionValue={getId}
                selected={filter.franchise}
                setSelected={makeSetSelected("franchise")}
              />
            )
          )}

          <FilterSection
            expandedByDefault={D.isNotEmpty(filter.signedType)}
            options={SignType.list()}
            label="Signing Types"
            getOptionLabel={SignType.toLabel}
            getOptionValue={F.identity}
            search={false}
            selected={filter.signedType}
            maxHeight={4.5}
            setSelected={makeSetSelected("signedType")}
          />

          {isLoading ? (
            <FilterSectionSkeleton width="70%" />
          ) : (
            Boolean(filters.productTypes.length) && (
              <FilterSection
                options={filters.productTypes}
                label="Product Types"
                getOptionLabel={getLabel}
                getOptionValue={getId}
                selected={filter.type}
                setSelected={makeSetSelected("type")}
              />
            )
          )}

          <PriceRangeFilter
            filter={filter}
            setFilterAndRestoreRank={setFilterAndRestoreRank}
          />

          {isFiltered && (
            <button
              type="button"
              className={s.clearAllBtn}
              onClick={handleClearAll}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}

FilterPanel.propTypes = {
  filter: PropTypes.shape({
    franchise: PropTypes.object,
    signedType: PropTypes.object,
    type: PropTypes.object,
    minPrice: PropTypes.number,
    maxPrice: PropTypes.number,
  }).isRequired,
  setFilter: PropTypes.func.isRequired,
  showFilters: PropTypes.bool.isRequired,
};
